import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import ConfirmationModal from '../business/store/ConfirmationModal.jsx';
import useAuth from '../hooks/AuthHook.jsx';
import { postAbnAndAhpra } from '../Api.js';
import { CREDENTIAL_SOURCES, LOCUM_CREDENTIALS } from '../Constants';
import { formatMissingCredentials, getMissingCredentials } from '../../helpers/CredentialUtils.js';
import { LOCUM_NAVIGATION_BUTTONS } from './LocumNavigationSidebar.jsx';
import PiiDeclaration from './PiiDeclaration.jsx';
import './CredentialsPromptModal.scss';

const { ABN, AHPRA, PII } = LOCUM_CREDENTIALS;

/**
 * Title and intro for the prompt, naming only the credentials the locum is missing.
 * The login reminder (LOGIN_PROMPT) also tells them they can do this later; the request prompt is a direct ask.
 * @param {string} source - CREDENTIAL_SOURCES value
 * @param {Array<string>} missing - LOCUM_CREDENTIALS values shown in the prompt
 */
const getPromptCopy = (source, missing) => {
  const missingText = formatMissingCredentials(missing);
  if (source === CREDENTIAL_SOURCES.LOGIN_PROMPT) {
    const piiOnly = missing.length === 1 && missing[0] === PII;
    return {
      title: piiOnly ? 'Confirm your Professional Indemnity Insurance' : 'Complete your details',
      intro: `You'll need to provide ${missingText} before you can request new shifts. You can also do this anytime from your account.`,
    };
  }
  return {
    title: 'Almost there...',
    intro: `To request shifts, please provide ${missingText}.`,
  };
};

/**
 * Inline (non-blocking) note shown next to a Request button after the locum cancels the credentials prompt.
 * Links to Manage Account, which has ABN, AHPRA and PII on both desktop and mobile.
 * @param {Array<string>} missing - LOCUM_CREDENTIALS values still missing
 */
export const MissingCredentialsNote = ({ missing }) => {
  return (
    <p className={'missing-credentials-note'}>
      {`You can't request shifts until you provide ${formatMissingCredentials(missing)}. You can do this anytime in `}
      <Link to={LOCUM_NAVIGATION_BUTTONS.ACCOUNT.path}>{'your account'}</Link>
      {'.'}
    </p>
  );
};

/**
 * Prompt that collects whichever of ABN, AHPRA and PII the locum is missing, saves them, then calls onComplete.
 * ABN and AHPRA fields only appear when empty; PII appears unless the locum has a passing declaration.
 * Used as the blocking prompt when requesting shifts (CartPage, Shift) and as the dismissible login reminder.
 * A "No" PII answer is still saved, since every attestation is recorded, but the locum stays blocked.
 * @param {Function} close - called whenever the prompt closes (saved or cancelled)
 * @param {Function} onComplete - called after saving once nothing is missing, e.g. to continue the request
 * @param {Function} onCancel - called with the credentials still missing when the locum cancels
 * @param {string} source - CREDENTIAL_SOURCES value, recorded on the audit log and used to pick the copy
 * @param {Array<string>} missing - extra LOCUM_CREDENTIALS from a 403 response, merged with the profile's list
 */
const CredentialsPromptModal = ({
  close,
  onComplete = () => {},
  onCancel = () => {},
  source,
  missing = [],
}) => {
  const { profile, setProfile } = useAuth();
  // Fixed when the prompt opens so fields don't disappear while the locum is filling them in.
  const [fieldsToShow] = useState(() => [...new Set([...getMissingCredentials(profile), ...missing])]);
  // What is still missing after any save (e.g. a "No" PII answer), reported to onCancel.
  const [remainingMissing, setRemainingMissing] = useState(fieldsToShow);
  const [abn, setAbn] = useState(profile.abn || '');
  const [ahpra, setAhpra] = useState(profile.ahpra || '');
  const [piiDeclaration, setPiiDeclaration] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorText, setErrorText] = useState('');
  const showAbn = fieldsToShow.includes(ABN);
  const showAhpra = fieldsToShow.includes(AHPRA);
  const showPii = fieldsToShow.includes(PII);
  const canSubmit = (!showAbn || abn.trim().length > 0)
    && (!showAhpra || ahpra.trim().length > 0)
    && (!showPii || piiDeclaration !== null);
  const { title, intro } = getPromptCopy(source, fieldsToShow);

  const cancel = () => {
    close();
    onCancel(remainingMissing);
  };

  const confirm = () => {
    setLoading(true);
    setErrorText('');
    postAbnAndAhpra(abn.trim(), ahpra.trim(), showPii ? piiDeclaration : undefined, source)
      .then(({ credentials }) => {
        setProfile({ ...profile, ...credentials });
        setLoading(false);
        setRemainingMissing(credentials.missingCredentials);
        if (credentials.missingCredentials.length === 0) {
          close();
          onComplete();
        }
        else if (source === CREDENTIAL_SOURCES.LOGIN_PROMPT) {
          // The login reminder is non-blocking: a saved "No" is accepted and we only block at request time.
          close();
        }
        else {
          setErrorText(`You can't request shifts until you provide ${formatMissingCredentials(credentials.missingCredentials)}.`);
        }
      })
      .catch((err) => {
        console.error(err);
        setLoading(false);
        setErrorText('Something went wrong saving your details. Please try again.');
      });
  };

  return (
    <ConfirmationModal
      loading={loading}
      close={cancel}
      additionalClassName={'credentials-prompt'}
      confirm={confirm}
      confirmText={'Save and continue'}
      disabled={!canSubmit}
    >
      <h3 className={'confirmation-title'}>{title}</h3>
      <p className={'confirmation-text'}>{intro}</p>
      {showAbn &&
        <label className={'credential-field'}>
          <span className={'credential-label'}>{'ABN'}</span>
          <input className={'credential-input'} value={abn} onChange={(e) => setAbn(e.target.value)} placeholder={'Your ABN'} />
        </label>
      }
      {showAhpra &&
        <label className={'credential-field'}>
          <span className={'credential-label'}>{'AHPRA'}</span>
          <input className={'credential-input'} value={ahpra} onChange={(e) => setAhpra(e.target.value)} placeholder={'Your AHPRA number'} />
        </label>
      }
      {showPii && <PiiDeclaration value={piiDeclaration} onChange={setPiiDeclaration} name={'credentials-prompt-pii'} disabled={loading} />}
      {errorText && <p className={'error-text'}>{errorText}</p>}
    </ConfirmationModal>
  );
};

export default CredentialsPromptModal;
